import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import QRCode from 'qrcode';
import {
  Alert,
  Box,
  Button,
  Logo,
  Spinner,
  TextField,
  Typography,
  portalChrome,
} from '@glplus/glpweb-dsl';
import {
  answerChallenge,
  hasSession,
  safeNextPath,
  startSignIn,
  type LoginStage,
} from '../lib/authFlow';

const CHROME = portalChrome.admin;

/**
 * /login — the portal-owned employee-pool sign-in (approved Figma frames
 * 16 / 16b / 16c). Rendered OUTSIDE usePortalGuard (the guard redirects
 * here, so guarding it would loop). Stages: credentials → (first sign-in:
 * new password → TOTP setup) → TOTP code → redirect to ?next.
 *
 * Staff accounts are invite-only (Staff & roles → Assign); there is no
 * self-signup and no self-serve password reset in v1 — an expired invite
 * is re-sent by the platform owner.
 */

type Feedback =
  | { severity: 'error' | 'warning'; title?: string; body: string }
  | null;

const FIELD_LABEL_SX = { fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 } as const;
const PRIMARY_BTN_SX = {
  backgroundColor: '#f06500',
  '&:hover': { backgroundColor: '#d35400' },
  width: '100%',
} as const;

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  const [checking, setChecking] = useState(true);
  const [stage, setStage] = useState<LoginStage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busy, setBusy] = useState(false);

  // A live session skips the form entirely.
  useEffect(() => {
    void hasSession().then((live) => {
      if (live) window.location.assign(next);
      else setChecking(false);
    });
  }, [next]);

  const finish = () => window.location.assign(next);

  const run = async (fn: () => Promise<LoginStage>) => {
    setBusy(true);
    setFeedback(null);
    try {
      const out = await fn();
      if (out.kind === 'done') finish();
      else setStage(out);
    } catch (err) {
      setFeedback(mapError(err));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <Shell>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}>
          <Spinner aria-label="Checking session" />
        </Box>
      </Shell>
    );
  }

  return (
    <Shell>
      <LoginCard>
        {feedback && (
          <Alert severity={feedback.severity} title={feedback.title} sx={{ mb: 2 }}>
            {feedback.body}
          </Alert>
        )}
        {stage.kind === 'credentials' && (
          <CredentialsStage
            email={email}
            onEmail={setEmail}
            busy={busy}
            onSubmit={(password) => void run(() => startSignIn(email.trim(), password))}
          />
        )}
        {stage.kind === 'newPassword' && (
          <NewPasswordStage
            busy={busy}
            onSubmit={(pw) => void run(() => answerChallenge(pw, email))}
          />
        )}
        {stage.kind === 'totpSetup' && (
          <TotpSetupStage
            setupUri={stage.setupUri}
            secret={stage.secret}
            busy={busy}
            onSubmit={(code) => void run(() => answerChallenge(code, email))}
          />
        )}
        {stage.kind === 'totpCode' && (
          <TotpCodeStage
            email={email}
            busy={busy}
            onSubmit={(code) => void run(() => answerChallenge(code, email))}
            onStartOver={() => {
              setStage({ kind: 'credentials' });
              setFeedback(null);
              void navigate('/login', { replace: true });
            }}
          />
        )}
      </LoginCard>
    </Shell>
  );
}

// ─── Stages ──────────────────────────────────────────────────────────────────

function CredentialsStage({
  email,
  onEmail,
  busy,
  onSubmit,
}: {
  email: string;
  onEmail: (v: string) => void;
  busy: boolean;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState('');
  const ready = /.+@.+\..+/.test(email.trim()) && password.length > 0;
  return (
    <Stage
      title="Sign in"
      subtitle="GL Plus staff only — access is by invitation."
      onEnter={() => ready && !busy && onSubmit(password)}
    >
      <Field label="Email">
        <TextField
          ariaLabel="Email"
          size="small"
          type="email"
          placeholder="you@glplusinc.com"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Field>
      <Field label="Password">
        <PasswordField ariaLabel="Password" value={password} onChange={setPassword} />
      </Field>
      <Button
        ariaLabel="Sign in"
        buttonText={busy ? 'Signing in…' : 'Sign in'}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(password)}
      />
      <Typography sx={{ mt: 2, fontSize: '12px', color: '#98a2b3', textAlign: 'center' }}>
        Trouble signing in? Contact the platform owner.
      </Typography>
    </Stage>
  );
}

const PASSWORD_REQUIREMENTS: Array<[string, (pw: string) => boolean]> = [
  ['At least 12 characters', (pw) => pw.length >= 12],
  ['Upper & lower case letters', (pw) => /[a-z]/.test(pw) && /[A-Z]/.test(pw)],
  ['At least one number', (pw) => /\d/.test(pw)],
  ['At least one symbol', (pw) => /[^A-Za-z0-9]/.test(pw)],
];

function NewPasswordStage({ busy, onSubmit }: { busy: boolean; onSubmit: (pw: string) => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const allMet = PASSWORD_REQUIREMENTS.every(([, ok]) => ok(pw));
  const ready = allMet && pw === pw2;
  return (
    <Stage
      title="Create your password"
      subtitle="Your temporary password from the invite email is now used up — choose your real one."
      onEnter={() => ready && !busy && onSubmit(pw)}
    >
      <Field label="New password">
        <PasswordField ariaLabel="New password" value={pw} onChange={setPw} />
      </Field>
      <Field label="Confirm new password">
        <PasswordField ariaLabel="Confirm new password" value={pw2} onChange={setPw2} />
      </Field>
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {PASSWORD_REQUIREMENTS.map(([label, ok]) => (
          <Typography
            key={label}
            sx={{ fontSize: '12px', color: ok(pw) ? '#027a48' : '#6b7280' }}
          >
            ✓ {label}
          </Typography>
        ))}
        {pw2.length > 0 && pw !== pw2 && (
          <Typography sx={{ fontSize: '12px', color: '#b42318' }}>
            Passwords don’t match yet
          </Typography>
        )}
      </Box>
      <Button
        ariaLabel="Continue"
        buttonText={busy ? 'Saving…' : 'Continue'}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(pw)}
      />
    </Stage>
  );
}

function TotpSetupStage({
  setupUri,
  secret,
  busy,
  onSubmit,
}: {
  setupUri: string;
  secret: string;
  busy: boolean;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    void QRCode.toDataURL(setupUri, { margin: 1, width: 224 }).then(setQr);
  }, [setupUri]);
  const ready = /^\d{6}$/.test(code.replace(/\s/g, ''));
  const groupedSecret = secret.replace(/(.{4})/g, '$1 ').trim();
  return (
    <Stage
      title="Set up two-step verification"
      subtitle="Admin requires an authenticator app (Google Authenticator, 1Password, Authy…). Scan the code, then confirm."
      onEnter={() => ready && !busy && onSubmit(code.replace(/\s/g, ''))}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 1 }}>
        <Box
          sx={{
            width: 112,
            height: 112,
            flexShrink: 0,
            border: '1px solid #d0d5dd',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {qr ? (
            <img src={qr} alt="Authenticator setup QR code" width={110} height={110} />
          ) : (
            <Spinner aria-label="Generating QR code" size="small" />
          )}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1f2937' }}>
            Scan with your authenticator app
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#6b7280', mt: 0.5 }}>
            Can’t scan? Enter the setup key below instead.
          </Typography>
        </Box>
      </Box>
      <Field label="Setup key">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid #d0d5dd',
            borderRadius: 2,
            backgroundColor: '#f9fafb',
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            sx={{ fontSize: '13px', color: '#1f2937', flex: 1, wordBreak: 'break-all' }}
          >
            {groupedSecret}
          </Typography>
          <Button
            ariaLabel="Copy setup key"
            buttonText={copied ? 'Copied' : 'Copy'}
            variant="text"
            size="small"
            onClick={() => {
              void navigator.clipboard.writeText(secret).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }}
          />
        </Box>
      </Field>
      <Field label="6-digit code from the app">
        <TextField
          ariaLabel="6-digit code from the app"
          size="small"
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Field>
      <Button
        ariaLabel="Verify and finish"
        buttonText={busy ? 'Verifying…' : 'Verify & finish'}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(code.replace(/\s/g, ''))}
      />
    </Stage>
  );
}

function TotpCodeStage({
  email,
  busy,
  onSubmit,
  onStartOver,
}: {
  email: string;
  busy: boolean;
  onSubmit: (code: string) => void;
  onStartOver: () => void;
}) {
  const [code, setCode] = useState('');
  const ready = /^\d{6}$/.test(code.replace(/\s/g, ''));
  return (
    <Stage
      title="Two-step verification"
      subtitle={email || 'Enter the code from your authenticator app.'}
      onEnter={() => ready && !busy && onSubmit(code.replace(/\s/g, ''))}
    >
      <Field label="6-digit code">
        <TextField
          ariaLabel="6-digit code"
          size="small"
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Field>
      <Button
        ariaLabel="Verify"
        buttonText={busy ? 'Verifying…' : 'Verify'}
        variant="contained"
        disabled={busy || !ready}
        sx={{ ...PRIMARY_BTN_SX, mt: 2 }}
        onClick={() => onSubmit(code.replace(/\s/g, ''))}
      />
      <Typography sx={{ mt: 2, fontSize: '12px', color: '#98a2b3', textAlign: 'center' }}>
        Open your authenticator app for a fresh code.
      </Typography>
      <Button
        ariaLabel="Start over"
        buttonText="Start over"
        variant="text"
        size="small"
        sx={{ mt: 1, alignSelf: 'center' }}
        onClick={onStartOver}
      />
    </Stage>
  );
}

// ─── Chrome / layout (Figma frame 16) ────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Box
        component="header"
        sx={{
          background: CHROME.headerBg,
          color: '#fff',
          minHeight: 64,
          px: { xs: '16px', sm: '32px', lg: '40px' },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          '& img': { width: '100% !important', maxWidth: '150px', display: 'block' },
        }}
      >
        <Logo logoStyle="full" size="desktop" color={CHROME.logoVariant} />
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '15px', letterSpacing: 0.5 }}
        >
          Admin
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function LoginCard({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pt: { xs: 6, md: 12 }, pb: 8 }}>
      <Box
        sx={{
          width: 420,
          maxWidth: '100%',
          backgroundColor: '#fff',
          borderRadius: 4,
          boxShadow: '0 8px 24px -4px rgba(16,24,40,0.10)',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Stage({
  title,
  subtitle,
  onEnter,
  children,
}: {
  title: string;
  subtitle: string;
  onEnter: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onEnter();
      }}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1f2937' }}>{title}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#6b7280', mt: 0.5 }}>{subtitle}</Typography>
      {children}
      {/* Hidden submit so Enter works inside the form */}
      <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
    </Box>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

/** Password input with the DSL's built-in show/hide (eye) toggle. */
function PasswordField({
  ariaLabel,
  value,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      ariaLabel={ariaLabel}
      size="small"
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      endAdornment="visibility"
      handleIconClick={() => setVisible((v) => !v)}
      sx={{ width: '100%' }}
    />
  );
}

// ─── Error mapping (Figma frame 16c) ─────────────────────────────────────────

function mapError(err: unknown): Feedback {
  const name = (err as { name?: string })?.name ?? '';
  const message = (err as { message?: string })?.message ?? '';

  if (name === 'UnsupportedStepError') {
    return { severity: 'error', body: 'This account needs a sign-in step the portal doesn’t support. Contact the platform owner.' };
  }
  if (/temporary password has expired/i.test(message)) {
    return {
      severity: 'warning',
      title: 'This invite is no longer valid.',
      body: 'Temporary passwords last 3 days. Ask the platform owner to re-send your invite from Staff & roles.',
    };
  }
  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') {
    return {
      severity: 'error',
      title: 'Incorrect email or password.',
      body: 'Check both and try again. Accounts lock after repeated attempts.',
    };
  }
  if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') {
    return { severity: 'error', body: 'That code didn’t work — enter the newest code from your authenticator app.' };
  }
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
    return { severity: 'error', body: 'Too many attempts — wait a minute and try again.' };
  }
  return { severity: 'error', body: message || 'Something went wrong signing you in. Try again.' };
}
